import { api } from "@/lib/api";
import { inboxOwnerLabel, inboxPending, inboxDeleted, type InboxItem, type InboxOwner } from "@/lib/documentInbox";
import type { ScanJob } from "@/lib/scanners";

export type OwnerSort = "count" | "oldest";
export interface InboxOwnerPile { key: string; label: string; kind: "unassigned" | InboxItem["owner_kind"]; workerId: string | null; items: InboxItem[]; oldest: string }
export function ownerPileKey(item: InboxItem, jobs: ScanJob[]): string {
  const needsReview = item.scan_job_id && jobs.some((job) => job.id === item.scan_job_id && job.review_required);
  if (item.owner_kind === "radnik" && (!item.radnik_id || (needsReview && !item.scan_owner_confirmed))) return "unassigned";
  if (item.owner_kind === "gradiliste" && !item.gradiliste_id) return "unassigned";
  return item.owner_kind === "firma" ? "firma" : `${item.owner_kind}:${item.family_member_id ?? item.radnik_id ?? item.gradiliste_id}`;
}
export function sortOwnerItems(items: InboxItem[]) {
  return [...items].sort((a, b) => (a.proposed_type_name ?? "").localeCompare(b.proposed_type_name ?? "", "hr") || a.uploaded_at.localeCompare(b.uploaded_at) || a.id.localeCompare(b.id));
}
export function ownerPiles(items: InboxItem[], jobs: ScanJob[], sort: OwnerSort, includeDeleted = false): InboxOwnerPile[] {
  const groups = new Map<string, InboxOwnerPile>();
  items.filter((item) => inboxPending(item) || includeDeleted && inboxDeleted(item)).forEach((item) => {
    const key = ownerPileKey(item, jobs);
    const group = groups.get(key) ?? { key, kind: key === "unassigned" ? "unassigned" : item.owner_kind, label: key === "unassigned" ? "Nerazvrstano" : item.owner_kind === "firma" ? "Firma" : inboxOwnerLabel(item), workerId: key === "unassigned" ? null : item.radnik_id, items: [], oldest: item.uploaded_at };
    group.items.push(item); group.oldest = group.oldest < item.uploaded_at ? group.oldest : item.uploaded_at; groups.set(key, group);
  });
  const rank = { obitelj: 1, unassigned: 0, radnik: 1, firma: 2, gradiliste: 3 };
  return [...groups.values()].map((group) => ({ ...group, items: sortOwnerItems(group.items) })).sort((a, b) => rank[a.kind] - rank[b.kind] || (sort === "count" ? b.items.length - a.items.length : a.oldest.localeCompare(b.oldest)) || a.label.localeCompare(b.label, "hr"));
}
export async function assignInboxWorker(id: string, workerId: string) {
  return (await api.patch<InboxItem>(`/inbox/${id}/owner`, { radnik_id: workerId })).data;
}
export async function refreshInboxPresence(workerId: string, sessionId: string, signal: AbortSignal) {
  return (await api.post<{ users: { id: string; name: string }[] }>(`/inbox/workers/${workerId}/presence`, { session_id: sessionId }, { signal })).data.users;
}
export async function leaveInboxPresence(workerId: string, sessionId: string) {
  await api.delete(`/inbox/workers/${workerId}/presence`, { params: { session_id: sessionId } });
}

export type InboxMoveTarget = InboxOwner | { owner_kind: "radnik"; radnik_id: null };
export async function moveInboxItem(id: string, target: InboxMoveTarget) {
  return (await api.patch<InboxItem>(`/inbox/${id}/move`, target)).data;
}
